import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { toast } from "react-toastify";
import {
  getUserWatchlists,
  getWatchlistsSearchPage,
  getUserDefaultWatchlist,
  getWatchlistItems,
  setUserDefaultWatchlist,
  addStockToWatchlists,
  deleteWatchlistItem,
  reorderWatchlistItems,
} from "../../api/user";
import { cleanSymbol, sortCuratedLists, CORE_LIST_NAME } from "./watchlistFormat";
import useLeagueContext from "../../utils/useLeagueContext";
import {
  NO_ELIGIBLE_ASSETS_MESSAGE,
  blocksUseInTrade,
  fetchEligibleCount,
  forgetEligibleCount,
} from "../../utils/leagueListEligibility";
import ListPanel from "./ListPanel";
import MainCard from "./MainCard";
import CreateListModal from "./CreateListModal";
import ManageListsModal from "./ManageListsModal";
import ListSkeleton from "../Skeleton/ListSkeleton";
import { SkeletonBlock } from "../Skeleton/Skeleton";

const PAGE_SIZE = 3;
const WatchlistDesktop = forwardRef(function WatchlistDesktop(props, ref) {
  const [userLists, setUserLists] = useState([]);
  const [curatedLists, setCuratedLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);

  // Client Rev 2: "In a league, evaluate eligibility before allowing Use in
  // Trade. If a watchlist has zero eligible assets, disable Use in Trade and
  // show: No assets in this list are eligible for this league. Do not mark that
  // list Active. Do not change the current Trade wheel."
  //
  // A visitor previewing a league is not in it and keeps the main app's
  // behaviour — the same membership test the rest of the shell uses.
  const { slug: leagueSlug, game: leagueGameRaw, isMember } = useLeagueContext();
  const leagueGame = leagueSlug && isMember && leagueGameRaw ? leagueGameRaw : null;
  // undefined = not looked up yet (blocks nothing), Infinity = unrestricted.
  const [eligibleCount, setEligibleCount] = useState(undefined);
  // Bumped whenever the open list's contents change, so the count above is
  // recomputed rather than left describing the list as it was before the edit.
  const [eligibilityTick, setEligibilityTick] = useState(0);

  const [selectedListId, setSelectedListId] = useState(null);
  const [mode, setMode] = useState("view");

  const [previews, setPreviews] = useState({});
  const itemsCacheRef = useRef({});

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [listsLoading, setListsLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const requestRef = useRef(0);
  const addedRef = useRef(false);

  const buildPreview = (id, data) => {
    const its = Array.isArray(data?.items) ? data.items : [];
    return {
      symbols: its.slice(0, 3).map((it) => cleanSymbol(it.symbol)),
      count: data?.pagination?.total ?? its.length,
    };
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setListsLoading(true);
      try {
        const [uRes, cRes, dRes] = await Promise.all([
          getUserWatchlists(),
          getWatchlistsSearchPage(5),
          getUserDefaultWatchlist(),
        ]);
        if (cancelled) return;

        const uLists = Array.isArray(uRes?.data?.watchlists) ? uRes.data.watchlists : [];
        const cLists = Array.isArray(cRes?.data?.watchlists) ? cRes.data.watchlists : [];
        const defId = dRes?.data?.default_watchlist_id;
        setUserLists(uLists);
        setCuratedLists(cLists);
        setActiveListId(defId != null ? String(defId) : null);

        const all = [...uLists, ...cLists];

        let initial = null;
        const saved = localStorage.getItem("lastWatchlistId");
        if (saved) {
          const match = all.find((l) => String(l.id) === String(saved));
          if (match) initial = match.id;
          localStorage.removeItem("lastWatchlistId");
        }
        if (initial == null && defId != null) {
          initial = all.find((l) => String(l.id) === String(defId))?.id ?? null;
        }
        if (initial == null) initial = sortCuratedLists(cLists)[0]?.id ?? uLists[0]?.id ?? null;
        setSelectedListId(initial);

        if (!cancelled) setListsLoading(false);

        let selectedPromise = null;
        if (initial != null) {
          setItemsLoading(true);
          selectedPromise = getWatchlistItems(initial, 1, PAGE_SIZE);
        }

        if (selectedPromise) {
          try {
            const r = await selectedPromise;
            if (cancelled) return;
            const its = Array.isArray(r?.data?.items) ? r.data.items : [];
            const hm = r?.data?.pagination?.has_more ?? its.length === PAGE_SIZE;
            setItems(its);
            setPage(1);
            setHasMore(hm);
            itemsCacheRef.current[initial] = { items: its, page: 1, hasMore: hm };
            setPreviews((p) => ({ ...p, [initial]: buildPreview(initial, r?.data) }));
          } catch (e) {
            console.error("Failed to load initial list items", e);
          } finally {
            if (!cancelled) setItemsLoading(false);
          }
        }

        for (const l of all) {
          if (cancelled) break;
          if (l.id === initial || itemsCacheRef.current[l.id]) continue;
          try {
            const pr = await getWatchlistItems(l.id, 1, PAGE_SIZE);
            if (cancelled) break;
            const pits = Array.isArray(pr?.data?.items) ? pr.data.items : [];
            itemsCacheRef.current[l.id] = {
              items: pits,
              page: 1,
              hasMore: pr?.data?.pagination?.has_more ?? pits.length === PAGE_SIZE,
            };
            setPreviews((p) => ({ ...p, [l.id]: buildPreview(l.id, pr?.data) }));
          } catch (e) {
            /* preview is best-effort */
          }
        }
      } catch (e) {
        console.error("Failed to load watchlists", e);
        if (!cancelled) setListsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPage = async (id, pageNum, reqId, append) => {
    if (append) setLoadingMore(true);
    else setItemsLoading(true);
    try {
      const r = await getWatchlistItems(id, pageNum, PAGE_SIZE);
      if (reqId !== requestRef.current) return;
      const its = Array.isArray(r?.data?.items) ? r.data.items : [];
      const hm = r?.data?.pagination?.has_more ?? its.length === PAGE_SIZE;
      const total = r?.data?.pagination?.total ?? null;

      setItems((prev) => {
        const merged = append ? [...prev, ...its] : its;
        itemsCacheRef.current[id] = { items: merged, page: pageNum, hasMore: hm };
        return merged;
      });
      setPage(pageNum);
      setHasMore(append ? (its.length > 0 ? hm : false) : hm);
      setPreviews((p) => {
        const merged = itemsCacheRef.current[id]?.items || its;
        return {
          ...p,
          [id]: {
            symbols: merged.slice(0, 3).map((it) => cleanSymbol(it.symbol)),
            count: total ?? merged.length,
          },
        };
      });
    } catch (e) {
      if (reqId === requestRef.current) console.error("Failed to load items", e);
    } finally {
      if (reqId === requestRef.current) {
        if (append) setLoadingMore(false);
        else setItemsLoading(false);
      }
    }
  };

  const selectList = (id) => {
    const reqId = ++requestRef.current;
    setSelectedListId(id);
    setMode("view");
    const cached = itemsCacheRef.current[id];
    if (cached) {
      setItems(cached.items);
      setPage(cached.page);
      setHasMore(cached.hasMore);
      setItemsLoading(false);
    } else {
      setItems([]);
      fetchPage(id, 1, reqId, false);
    }
  };

  useImperativeHandle(ref, () => ({
    selectListByName: (name) => {
      const list = curatedLists.find(
        (l) => l.name.toLowerCase().trim() === name.toLowerCase().trim()
      );
      if (list) {
        selectList(list.id);
      }
    },
  }));

  const loadMore = () => {
    if (loadingMore || !hasMore || selectedListId == null || mode === "edit") return;
    fetchPage(selectedListId, page + 1, requestRef.current, true);
  };

  const allLists = [...userLists, ...curatedLists];
  const selectedList = allLists.find((l) => l.id === selectedListId) || null;
  const isCurated = curatedLists.some((c) => c.id === selectedListId);
  const isActive = selectedList != null && String(activeListId) === String(selectedList.id);
  const variant = isCurated ? "curated" : "user";
  const count = selectedListId != null ? previews[selectedListId]?.count ?? items.length : 0;

  // How many of the OPEN list's assets this league will trade. Re-asked whenever
  // the open list changes; outside a league this resolves without a request and
  // never blocks anything.
  useEffect(() => {
    if (!leagueGame || selectedListId == null) {
      setEligibleCount(undefined);
      return undefined;
    }
    let cancelled = false;
    setEligibleCount(undefined);
    fetchEligibleCount(leagueGame, selectedListId).then((n) => {
      if (!cancelled) setEligibleCount(n);
    });
    return () => {
      cancelled = true;
    };
  }, [leagueGame, selectedListId, eligibilityTick]);

  const useInTradeBlocked = blocksUseInTrade(eligibleCount);

  const activateList = async () => {
    if (selectedListId == null || isActive) return;
    // The button is already disabled once the count has arrived; this covers a
    // press that beats it. Returning here leaves the stored default — and so the
    // Trade wheel — exactly as it was.
    if (leagueGame) {
      const eligible = await fetchEligibleCount(leagueGame, selectedListId);
      if (blocksUseInTrade(eligible)) {
        setEligibleCount(0);
        toast.error("This list has no assets eligible for this league.");
        return;
      }
    }
    try {
      await setUserDefaultWatchlist({ default_watchlist_id: selectedListId });
      setActiveListId(String(selectedListId));
    } catch (e) {
      console.error(e);
      toast.error("Could not make this list active. Try again.");
    }
  };

  const removeAsset = async (item) => {
    try {
      await deleteWatchlistItem(item.id);
      // The count is derived from the list's contents, so it dies with them.
      forgetEligibleCount(leagueGame, selectedListId);
      setEligibilityTick((t) => t + 1);
      setItems((prev) => {
        const next = prev.filter((it) => it.id !== item.id);
        if (itemsCacheRef.current[selectedListId]) {
          itemsCacheRef.current[selectedListId].items = next;
        }
        return next;
      });
      setPreviews((p) => {
        const cur = p[selectedListId];
        if (!cur) return p;
        const nextCount = Math.max(0, (cur.count || 1) - 1);
        return { ...p, [selectedListId]: { ...cur, count: nextCount } };
      });
    } catch (e) {
      console.error(e);
      toast.error("Could not remove the asset. Try again.");
    }
  };

  const reorderAssets = async (newItems) => {
    setItems(newItems);
    if (itemsCacheRef.current[selectedListId]) itemsCacheRef.current[selectedListId].items = newItems;
    try {
      await reorderWatchlistItems(selectedListId, newItems.map((it) => it.id));
    } catch (e) {
      console.error(e);
      toast.error("Could not save the watchlist order. Try again.");
    }
  };

  const addAsset = async (symbol) => {
    if (selectedListId == null || !symbol) return;
    try {
      const res = await addStockToWatchlists(symbol, [selectedListId]);
      forgetEligibleCount(leagueGame, selectedListId);
      setEligibilityTick((t) => t + 1);
      if (res?.status === 200) {
        addedRef.current = true;
        delete itemsCacheRef.current[selectedListId];
        toast.success(`Added to ${selectedList.name}`);
      } else {
        toast.error("Could not add the asset to this watchlist. Try again.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not add the asset to this watchlist. Try again.");
    }
  };

  const exitAdd = () => {
    setMode("view");
    if (addedRef.current) {
      addedRef.current = false;
      const reqId = ++requestRef.current;
      fetchPage(selectedListId, 1, reqId, false);
    } else {
      const cached = itemsCacheRef.current[selectedListId];
      if (cached) {
        setItems(cached.items);
        setPage(cached.page);
        setHasMore(cached.hasMore);
      }
    }
  };

  const refreshUserLists = async () => {
    try {
      const r = await getUserWatchlists();
      const lists = Array.isArray(r?.data?.watchlists) ? r.data.watchlists : [];
      setUserLists(lists);
      return lists;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const handleCreated = async () => {
    setShowCreate(false);
    await refreshUserLists();
  };

  const handleDeleted = (id) => {
    const remainingUser = userLists.filter((l) => l.id !== id);
    setUserLists(remainingUser);
    delete itemsCacheRef.current[id];
    setPreviews((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });

    const coreId = sortCuratedLists(curatedLists)[0]?.id ?? null;
    const wasActive = String(activeListId) === String(id);
    if (wasActive && coreId != null) {
      setActiveListId(String(coreId));
      setUserDefaultWatchlist({ default_watchlist_id: coreId }).catch((e) =>
        console.error("Failed to restore the default watchlist", e)
      );
    }

    if (id === selectedListId) {
      const next = wasActive
        ? coreId
        : remainingUser[0]?.id ?? coreId;
      if (next != null) selectList(next);
      else {
        setSelectedListId(null);
        setItems([]);
      }
    }
  };

  const handleListReordered = (ordered) => setUserLists(ordered);
  const handleListRenamed = (id, name) =>
    setUserLists((prev) => prev.map((l) => (l.id === id ? { ...l, name } : l)));

  if (listsLoading) {
    return (
      <div
        className="grid grid-cols-[280px_minmax(0,1fr)] gap-6 py-2"
        aria-busy="true"
        aria-label="Watchlists loading"
      >
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <ListSkeleton rows={6} avatar={false} columns={0} rowClassName="py-3" />
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <SkeletonBlock height={20} width={180} />
          <ListSkeleton
            rows={7}
            avatarSize={36}
            columns={2}
            columnWidths={[76, 60]}
            className="mt-5"
            rowClassName="py-3.5"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[280px_minmax(0,1fr)] gap-6 py-2">
      <ListPanel
        userLists={userLists}
        curatedLists={curatedLists}
        previews={previews}
        selectedListId={selectedListId}
        activeListId={activeListId}
        onSelect={selectList}
        onCreate={() => setShowCreate(true)}
        onManage={() => setShowManage(true)}
      />

      <MainCard
        selectedList={selectedList}
        isCurated={isCurated}
        isActive={isActive}
        count={count}
        mode={mode}
        items={items}
        variant={variant}
        initialLoading={itemsLoading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        onRemove={removeAsset}
        onReorder={reorderAssets}
        onEnterEdit={() => setMode("edit")}
        onExitEdit={() => setMode("view")}
        onEnterAdd={() => {
          addedRef.current = false;
          setMode("add");
        }}
        onExitAdd={exitAdd}
        onAddAsset={addAsset}
        onUseInTrade={activateList}
        useInTradeBlocked={useInTradeBlocked}
        useInTradeBlockedMessage={NO_ELIGIBLE_ASSETS_MESSAGE}
      />

      {showCreate && (
        <CreateListModal onClose={() => setShowCreate(false)} onDone={handleCreated} />
      )}
      {showManage && (
        <ManageListsModal
          userLists={userLists}
          onClose={() => setShowManage(false)}
          onDeleted={handleDeleted}
          onReordered={handleListReordered}
          onRenamed={handleListRenamed}
        />
      )}
    </div>
  );
});

export default WatchlistDesktop;
